import React, { useMemo } from 'react';
import {
  HomeHeroAuthenticationStep,
  useHomeHeroAuthenticationStepData,
} from '@/store/authentication/AuthenticationStore';
import TwoFactorAuthenticationForm from '@/features/authentication/two-factor-authentication/TwoFactorAuthenticationForm';
import { useSelector } from 'react-redux';
import { selectAuthRecentlyLoggedOut } from '@/store/selectors/authSelectors';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import Registration from '@/features/authentication/registration/Registration';
import { FormattedMessage } from 'react-intl';
import { selectHeroWelcomeTextsHtml } from '@/store/cms/welcome-texts/selectors';
import HeroTransparentCardLayout from '@/components/shared/hero/HeroTransparentCardLayout';
import Login from '@/features/authentication/login/Login';
import CmsStyledContent from '@/components/shared/CmsStyledContent';
import classNames from 'classnames';
import { envIsCanada } from '@/constants/constants';
import AuthenticationIntro from '@/features/authentication/authentication-intro/AuthenticationIntro';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

const HomeHeroAuthentication = () => {
  const { homeHeroAuthenticationStep } = useHomeHeroAuthenticationStepData();
  const hasRecentlyLoggedOut = useSelector(selectAuthRecentlyLoggedOut);
  const { isEnabled: isRewardsReelEnabled } = useRewardsWheel();
  const welcomeOfferHeroText = useSelector(selectHeroWelcomeTextsHtml);
  const isRegistrationRemoved = useRestrictedMode();

  // Registration is removed in restricted mode — the hero would otherwise default to the
  // Registration step, so fall back to Login (existing users can still sign in).
  const effectiveStep =
    isRegistrationRemoved && homeHeroAuthenticationStep === HomeHeroAuthenticationStep.Registration
      ? HomeHeroAuthenticationStep.Login
      : homeHeroAuthenticationStep;

  // This represents just the heading title above the authentication form
  const heading = useMemo(() => {
    const getHeadingId = () => {
      switch (effectiveStep) {
        case HomeHeroAuthenticationStep.Login:
          return isRewardsReelEnabled && hasRecentlyLoggedOut // special handling for the state when user logs out and rewards reel is enabled
            ? 'authentication.login.header.rewardsWheelReminder'
            : 'authentication.login.header';
        case HomeHeroAuthenticationStep.TwoFactorAuthentication:
          return 'authentication.two-factor-authentication.header';
        default:
          return 'authentication.forgot-password-success.header';
      }
    };

    if (effectiveStep === HomeHeroAuthenticationStep.Registration) {
      return (
        <CmsStyledContent
          text={welcomeOfferHeroText}
          stylesOverride={classNames(
            '[&_p]:text-lg [&_p]:sm:text-2xl',
            '[&_h1]:my-4 [&_h1]:text-4xl [&_h1]:sm:text-5xl',
            '[&_h2]:leading-[1.2] [&_h2]:text-xl [&_h2]:md:text-4xl',
          )}
        />
      );
    }

    return (
      <FormattedMessage
        id={getHeadingId()}
        values={{
          span: str => <span className="text-blue-lighter">{str}</span>,
        }}
      />
    );
  }, [effectiveStep, isRewardsReelEnabled, hasRecentlyLoggedOut, welcomeOfferHeroText]);

  const AuthenticationStep = useMemo(() => {
    switch (effectiveStep) {
      case HomeHeroAuthenticationStep.Login:
        return Login;
      case HomeHeroAuthenticationStep.TwoFactorAuthentication:
        return TwoFactorAuthenticationForm;
      default:
        return Registration;
    }
  }, [effectiveStep]);

  // This represents the entire intro section being shown
  // instead of the authentication form if conditions apply
  if (envIsCanada()) {
    // Only shown in Ontario currently
    return <AuthenticationIntro />;
  }

  return (
    <HeroTransparentCardLayout heading={heading}>
      <AuthenticationStep />
    </HeroTransparentCardLayout>
  );
};

export default HomeHeroAuthentication;
